import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Routes, Route, Navigate, useSearchParams, useLocation, Outlet, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { LaunchAnimation } from './components/launch-animation';
import { BiometricGate } from './components/biometric-gate';
import { BiometricEnrollPrompt } from './components/biometric-enroll-prompt';
import { useAppStore } from './stores/app-store';
import { useAuthStore } from './stores/auth-store';
import { useNotificationsStore } from './stores/notifications-store';
import { useTheme } from './hooks/use-theme';
import { useKeyboardShortcuts } from './hooks/use-keyboard-shortcuts';
import { formatCombo, type ShortcutHelpSection } from './hooks/use-editor-shortcuts';
import { useShortcutsStore } from './stores/shortcuts-store';
import { initFcm } from './lib/fcm';
import { AppShell } from './components/app-shell';
import { OwnerDashboard } from './components/owner-dashboard';
import { MangaEditor } from './components/manga-editor';
import { ShortcutsHelp } from './components/shortcuts-help';
import { ProjectsView } from './components/projects-view';
import { EditorView } from './components/editor-view';
import { ExportView } from './components/export-view';
import { SettingsView } from './components/settings-view';
import { StatsView } from './components/stats-view';
import { MoonLoader } from './components/ui/moon-loader';
import { ProfessionalsView } from './components/professionals-view';
import { MessagesView } from './components/messages-view';
import { PricingView } from './components/pricing-view';
import { AccountView } from './components/account-view';
import { NotificationsView } from './components/notifications-view';
// StoryBibleView and OutlineBoardView are now rendered inside EditorView workspace tabs.
// Deep-link routes (/app/bible/:projectId, /app/outline/:projectId) redirect to the editor.
import { GuidedCreateView } from './components/guided-create-view';
import { LoginPage } from './pages/login';
import { RegisterPage } from './pages/register';
import { ForgotPasswordPage } from './pages/forgot-password';
import { ResetPasswordPage } from './pages/reset-password';
import { VerifyEmailPage } from './pages/verify-email';
import { LandingPage } from './pages/landing-page';
import { LandingPageCinematic } from './pages/landing-page-cinematic';
import { LandingStoryPage } from './pages/landing-story';
import { PublicProfile } from './pages/public-profile';
import { MobileOnboarding } from './pages/mobile-onboarding';
import { CinematicLanding } from './components/cinematic-landing';
import { CinematicLab } from './components/cinematic-lab';
import { PricingPage } from './pages/pricing';
import { InfoPage } from './pages/marketing/info-page';
import { PrivacyPage } from './pages/legal/privacy';
import { TermsPage } from './pages/legal/terms';
import { InviteAcceptPage } from './pages/invite-accept';
import { Toaster } from './components/ui/sonner';
import { OfflineBanner } from './components/offline-banner';
import { Loader2 } from 'lucide-react';

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

const pageTransition = {
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1] as const,
};

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <MoonLoader size={34} />
    </div>
  );
}

type AuthPage = 'login' | 'register';

// Layout wrapper for authenticated app with Outlet for nested routes
function AppLayout() {
  const fetchProjects = useAppStore((s) => s.fetchProjects);
  const addNotification = useNotificationsStore((s) => s.addNotification);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  // Context bindings published by whichever editor is active (see shortcuts-store).
  const contextSections = useShortcutsStore((s) => s.contextSections);
  const contextTitle = useShortcutsStore((s) => s.contextTitle);

  // Initialize theme on mount
  useTheme();

  // Fetch projects from API on mount
  useEffect(() => {
    fetchProjects().catch((error) => {
      console.error('Failed to fetch projects:', error);
    });
  }, [fetchProjects]);

  // Initialize FCM for push notifications (graceful no-op when env vars absent)
  useEffect(() => {
    initFcm(addNotification).catch((error) => {
      console.warn('FCM init error:', error);
    });
  }, [addNotification]);

  const handleCloseShortcutsHelp = useCallback(() => {
    setShortcutsHelpOpen(false);
  }, []);

  // Toolbar "?" buttons (deep in the editors) open the overlay via this event,
  // so the single open-state can stay here at the app root.
  useEffect(() => {
    const open = () => setShortcutsHelpOpen(true);
    window.addEventListener('reformu:shortcuts-open', open);
    return () => window.removeEventListener('reformu:shortcuts-open', open);
  }, []);

  // Base app-navigation bindings — always shown in the overlay, below whatever
  // the active editor contributed. Built from the same `formatCombo` the editor
  // registry uses, so the whole overlay reads consistently.
  const appBaseSections = useMemo<ShortcutHelpSection[]>(() => [
    {
      group: 'global',
      items: [
        { id: 'app-dashboard', combos: [formatCombo('mod+1')], description: t('shortcuts.appDashboard', 'Ir para o Painel') },
        { id: 'app-projects', combos: [formatCombo('mod+2')], description: t('shortcuts.appProjects', 'Ir para Projetos') },
        { id: 'app-messages', combos: [formatCombo('mod+3')], description: t('shortcuts.appMessages', 'Ir para Mensagens') },
        { id: 'app-stats', combos: [formatCombo('mod+4')], description: t('shortcuts.appStats', 'Ir para Estatísticas') },
        { id: 'app-help', combos: [formatCombo('?')], description: t('shortcuts.appHelp', 'Abrir/fechar esta ajuda') },
      ],
    },
  ], [t]);

  const helpSections = useMemo<ShortcutHelpSection[]>(
    () => [...contextSections, ...appBaseSections],
    [contextSections, appBaseSections],
  );

  // Global keyboard shortcuts
  useKeyboardShortcuts([
    {
      // ? (shift+/) → open shortcuts help overlay
      combo: '?',
      handler: () => setShortcutsHelpOpen((o) => !o),
    },
    {
      // Esc → close shortcuts help overlay (other overlays handle their own Esc)
      combo: 'esc',
      handler: () => setShortcutsHelpOpen(false),
      when: () => shortcutsHelpOpen,
    },
    {
      // mod+1 → Dashboard
      combo: 'mod+1',
      handler: () => navigate('/app'),
    },
    {
      // mod+2 → Projects
      combo: 'mod+2',
      handler: () => navigate('/app/projects'),
    },
    {
      // mod+3 → Messages
      combo: 'mod+3',
      handler: () => navigate('/app/messages'),
    },
    {
      // mod+4 → Stats
      combo: 'mod+4',
      handler: () => navigate('/app/stats'),
    },
    {
      // mod+k → navigate to dashboard (command-style)
      combo: 'mod+k',
      handler: () => navigate('/app'),
    },
  ]);

  return (
    <>
      <AppShell>
        {/* Crossfade sólido: a página que entra se sobrepõe à que sai (sem frame em branco) */}
        <div className="relative flex-1 overflow-hidden">
          <AnimatePresence initial={false}>
            <motion.div
              key={location.pathname}
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="absolute inset-0 flex flex-col overflow-hidden bg-background"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </AppShell>
      <Toaster />
      <ShortcutsHelp
        open={shortcutsHelpOpen}
        onClose={handleCloseShortcutsHelp}
        sections={helpSections}
        title={contextTitle}
      />
    </>
  );
}

// Loading component for projects
function ProjectsLoader() {
  const isLoading = useAppStore((s) => s.isLoading);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <ProjectsView />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, checkAuth } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [authPage, setAuthPage] = useState<AuthPage>(() => {
    const auth = searchParams.get('auth');
    return auth === 'signup' ? 'register' : 'login';
  });

  // Initialize theme on mount
  useTheme();

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Show loading screen while checking auth
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Show auth pages if not authenticated
  if (!isAuthenticated) {
    if (authPage === 'login') {
      return (
        <>
          <LoginPage onNavigateToRegister={() => setAuthPage('register')} />
          <Toaster />
        </>
      );
    }
    return (
      <>
        <RegisterPage onNavigateToLogin={() => setAuthPage('login')} />
        <Toaster />
      </>
    );
  }

  return <>{children}</>;
}

/**
 * RootRoute — decides what "/" renders.
 *
 * The onboarding/welcome is ONLY for the installed native app (Capacitor):
 *   - Authenticated              → redirect /app
 *   - Unauthenticated, seen flag → redirect /app  (ProtectedRoute shows login)
 *   - Unauthenticated, first run → <MobileOnboarding />  (welcome → login)
 *
 * Any browser (desktop OR mobile) always gets the normal landing page —
 * a mobile-browser visitor is a prospect who should see the marketing site
 * (with the app download), not the in-app onboarding.
 */
function RootRoute() {
  const isNative = Capacitor.isNativePlatform();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // Browser (desktop or mobile): the narrative landing (/historia).
  // The previous cinematic landing stays reachable at /cinematic.
  if (!isNative) return <LandingStoryPage />;

  // Native app: authenticated (persisted) → straight to the app.
  if (isAuthenticated) return <Navigate to="/app" replace />;

  // Native app: onboarding already completed → skip to login.
  const seen =
    typeof window !== 'undefined' && !!localStorage.getItem('onboarding-seen');
  if (seen) return <Navigate to="/app" replace />;

  // Native app, first run → welcome/onboarding carousel.
  return <MobileOnboarding />;
}

/**
 * BibleDeepLink — keeps /app/bible/:projectId working by redirecting to the
 * editor workspace with the Bible tab pre-selected (?ws=bible).
 * /app/bible (no projectId) → /app/projects (ambiguous, pick a book first).
 */
function BibleDeepLink() {
  const { projectId } = useParams<{ projectId?: string }>();
  if (!projectId) return <Navigate to="/app/projects" replace />;
  return <Navigate to={`/app/editor/${projectId}?ws=bible`} replace />;
}

/**
 * OutlineDeepLink — same pattern for /app/outline/:projectId.
 */
function OutlineDeepLink() {
  const { projectId } = useParams<{ projectId?: string }>();
  if (!projectId) return <Navigate to="/app/projects" replace />;
  return <Navigate to={`/app/editor/${projectId}?ws=outline`} replace />;
}

function AnimatedRoutes() {
  const location = useLocation();

  // Always start a new page at the TOP, instantly (never an animated scroll).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  // NOTE: No <AnimatePresence>/key wrapper around <Routes> here.
  // Keying <Routes> by pathname remounts the entire tree (incl. AppShell/sidebar)
  // on every navigation. Per-page transitions live inside AppLayout (keyed by
  // pathname around the <Outlet/>), so the /app layout stays mounted across tabs.
  return (
    <Routes location={location}>
        {/* Root route — cinematic landing on desktop, onboarding carousel on mobile */}
        <Route
          path="/"
          element={<RootRoute />}
        />
        {/* Cinematic landing also reachable at /cinematic */}
        <Route
          path="/cinematic"
          element={<LandingPageCinematic />}
        />
        {/* Narrative scrollytelling PROPOSAL landing — parallel to the real landing */}
        <Route
          path="/historia"
          element={<LandingStoryPage />}
        />
        {/* Original (non-cinematic) landing kept as a backup for now */}
        <Route
          path="/landing-original"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
            >
              <LandingPage />
            </motion.div>
          }
        />
        {/* Original cinematic mock (moonscribe) kept for reference */}
        <Route
          path="/cinematic-mock"
          element={<CinematicLanding />}
        />
        {/* Cinematic LAB — built FROM the mock, adapted by parts (step 1: real header/footer) */}
        <Route
          path="/cinematic-lab"
          element={<CinematicLab />}
        />
        <Route
          path="/pricing"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <PricingPage />
            </motion.div>
          }
        />

        {/* Perfil público cinemático (estante em destaque, estilo showcase) */}
        <Route path="/perfil" element={<PublicProfile />} />

        {/* Auth pages as standalone routes */}
        <Route
          path="/login"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <LoginPage onNavigateToRegister={() => {}} />
              <Toaster />
            </motion.div>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <ForgotPasswordPage />
              <Toaster />
            </motion.div>
          }
        />
        <Route
          path="/reset-password"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <ResetPasswordPage />
              <Toaster />
            </motion.div>
          }
        />
        <Route
          path="/verify-email"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <VerifyEmailPage />
              <Toaster />
            </motion.div>
          }
        />

        {/* Public marketing pages */}
        {[
          'about',
          'blog',
          'careers',
          'contact',
          'status',
          'author-trust',
          'join-professional',
          'freelancer-success',
          'community',
          'services',
        ].map((slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
                transition={pageTransition}
                className="min-h-dvh"
              >
                <InfoPage slug={slug} />
              </motion.div>
            }
          />
        ))}

        {/* Legal pages — /termos and /privacidade (canonical pt-BR URLs) */}
        {/* Also handle /terms and /privacy for backward compatibility */}
        {(['termos', 'terms'] as const).map((slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
                transition={pageTransition}
                className="min-h-dvh"
              >
                <TermsPage />
              </motion.div>
            }
          />
        ))}
        {(['privacidade', 'privacy'] as const).map((slug) => (
          <Route
            key={slug}
            path={`/${slug}`}
            element={
              <motion.div
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageVariants}
                transition={pageTransition}
                className="min-h-dvh"
              >
                <PrivacyPage />
              </motion.div>
            }
          />
        ))}

        {/* Protected app routes with nested routing */}
        <Route
          path="/app"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            </motion.div>
          }
        >
          {/* Default route - Dashboard */}
          <Route index element={<StatsView />} />

          {/* Main navigation routes */}
          <Route path="projects" element={<ProjectsLoader />} />
          <Route path="editor/:projectId" element={<EditorView />} />
          <Route path="manga/:projectId" element={<MangaEditor />} />
          <Route path="export/:projectId" element={<ExportView />} />
          <Route path="settings" element={<SettingsView />} />
          {/* Painel do dono — gateado no backend por ADMIN_EMAIL */}
          <Route path="owner" element={<OwnerDashboard />} />
          {/* Início absorveu as estatísticas; rota antiga redireciona */}
          <Route path="stats" element={<Navigate to="/app" replace />} />
          <Route path="professionals" element={<ProfessionalsView />} />
          <Route path="messages" element={<MessagesView />} />
          <Route path="notifications" element={<NotificationsView />} />
          <Route path="account" element={<AccountView />} />
          {/* bible/outline are now workspace tabs inside the editor, not standalone routes.
              Deep links still work; context-less paths redirect to the project picker. */}
          <Route path="bible" element={<Navigate to="/app/projects" replace />} />
          <Route path="bible/:projectId" element={<BibleDeepLink />} />
          <Route path="outline" element={<Navigate to="/app/projects" replace />} />
          <Route path="outline/:projectId" element={<OutlineDeepLink />} />
          <Route path="create" element={<GuidedCreateView />} />

          {/* Fallback for unknown /app routes */}
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Route>

        {/* Invite accept page */}
        <Route
          path="/invite/:token"
          element={
            <motion.div
              initial="initial"
              animate="animate"
              exit="exit"
              variants={pageVariants}
              transition={pageTransition}
              className="min-h-dvh"
            >
              <InviteAcceptPage />
              <Toaster />
            </motion.div>
          }
        />

        {/* Global fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
}

export default function App() {
  // True only on native — the browser never sees the launch animation.
  const [showLaunch, setShowLaunch] = useState<boolean>(() => Capacitor.isNativePlatform());

  // Hide the native Capacitor splash screen once React has mounted.
  // Dynamic import keeps @capacitor/splash-screen out of the browser bundle;
  // the isNativePlatform() guard makes this a no-op in the browser.
  // Fire early (~250 ms) so the static native splash hands off to our dark
  // web LaunchAnimation with no visible gap (both backgrounds are #0C1020).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const timer = window.setTimeout(() => {
      import('@capacitor/splash-screen')
        .then(({ SplashScreen }) => {
          SplashScreen.hide({ fadeOutDuration: 400 }).catch(() => {});
        })
        .catch(() => {});
    }, 250);

    return () => window.clearTimeout(timer);
  }, []);

  // OTA self-update (native): confirm this bundle is healthy, then check for a
  // newer web bundle. Checa no boot E toda vez que o app volta ao primeiro
  // plano (appStateChange) — no Android o "cold start" real é raro (fechar dos
  // recentes não mata o processo), então sem o resume o OTA nunca re-executava.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cleanup: (() => void) | undefined;
    void (async () => {
      const { notifyAppReady, announceUpdateIfApplied, checkAndApplyUpdate } = await import('./lib/ota');
      await notifyAppReady();
      await announceUpdateIfApplied();
      await checkAndApplyUpdate();
      const { App: CapApp } = await import('@capacitor/app');
      const sub = await CapApp.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void checkAndApplyUpdate();
      });
      cleanup = () => { sub.remove().catch(() => {}); };
    })();
    return () => cleanup?.();
  }, []);

  // Android hardware BACK button — native only.
  // Dynamic import keeps @capacitor/app out of the browser bundle.
  // If there is history to go back through, navigate back; otherwise minimize
  // (exitApp) rather than doing nothing, so the OS Back still feels natural.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cleanup: (() => void) | undefined;

    import('@capacitor/app')
      .then(({ App: CapApp }) => {
        const sub = CapApp.addListener('backButton', ({ canGoBack }) => {
          const isAtRoot =
            window.location.pathname === '/app' ||
            window.location.pathname === '/';

          if (canGoBack && !isAtRoot) {
            window.history.back();
          } else {
            // At the app root: exit (minimize) cleanly instead of getting stuck.
            CapApp.exitApp().catch(() => {});
          }
        });

        // addListener returns a Promise<PluginListenerHandle>
        sub.then((handle) => {
          cleanup = () => handle.remove().catch(() => {});
        }).catch(() => {});
      })
      .catch(() => {});

    return () => cleanup?.();
  }, []);

  // Edge-swipe-back: horizontal swipe starting within ~24 px of the LEFT
  // screen edge and moving right ≥ 80 px → navigate(-1).
  // Only active on touch devices (native or mobile browser).
  // Does NOT fire if the touch started outside the 24 px edge zone,
  // so it never conflicts with horizontal carousels or normal scrolling.
  useEffect(() => {
    // Only needed on touch-capable devices; no-op on desktop browsers.
    if (!('ontouchstart' in window)) return;

    let startX = -1;
    let startY = -1;
    let tracking = false;

    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch) return;
      // Only track when the gesture starts within the left-edge zone (24 px).
      if (touch.clientX <= 24) {
        startX = touch.clientX;
        startY = touch.clientY;
        tracking = true;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;

      const touch = e.changedTouches[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      // Must be more horizontal than vertical and travel ≥ 80 px to the right.
      if (dx >= 80 && dy < dx * 0.8) {
        window.history.back();
      }

      startX = -1;
      startY = -1;
    };

    const onTouchCancel = () => {
      tracking = false;
      startX = -1;
      startY = -1;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, []);

  return (
    <>
      {/* Launch animation — native only, every cold start, above the router */}
      <AnimatePresence>
        {showLaunch && <LaunchAnimation onDone={() => setShowLaunch(false)} />}
      </AnimatePresence>
      {/* Biometric unlock gate (native + enabled) — fingerprint on open */}
      <BiometricGate>
        <AnimatedRoutes />
      </BiometricGate>
      {/* Offer to enable fingerprint unlock after login (native) */}
      <BiometricEnrollPrompt />
      <OfflineBanner />
    </>
  );
}
